import { forwardRef, type ReactElement, type Ref } from 'react';

import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../../../core/types/data-props.js';
import { StylingProps } from '../../../../core/types/styling-props.js';
import { WithChildren } from '../../../../core/types/with-children.js';
import { Flex } from '../../../../layouts/flex/Flex.js';

/**
 * @public
 */
export type DataTableTableActionsProps = WithChildren &
  StylingProps &
  DataTestId &
  BehaviorTrackingProps;

/**
 * A helper function to check whether the child component is the TableActions component
 * @internal
 */
export function _isTableActions(
  child: ReactElement | null,
): child is ReactElement<DataTableTableActionsProps> {
  return child?.type === DataTableTableActions;
}

/**
 * TableActions slot component for general actions that affect the entire table. Positoned above the header.
 * @public
 */
export const DataTableTableActions = forwardRef(
  (
    props: DataTableTableActionsProps,
    forwardedRef: Ref<HTMLDivElement>,
  ): ReactElement | null => {
    const {
      children,
      'data-testid': dataTestId,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    return (
      <Flex
        ref={forwardedRef}
        justifyContent="flex-start"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={4}
        data-testid={dataTestId}
        className={consumerClassName}
        style={consumerStyle}
        {...behavioralTrackingProps}
        {...restProps}
      >
        {children}
      </Flex>
    );
  },
);

(
  DataTableTableActions as typeof DataTableTableActions & {
    displayName: string;
  }
).displayName = 'DataTable.TableActions';
